import api from '../../../services/api';
import { withReturnTo } from '../../../utils/returnTo';

// EVERY CALL THE PLUGINS SECTION MAKES, scoped to the space you are in: a
// person's own (no group) or a group's (`?group_id=` / a body's `group_id`, or
// the /groups/<g>/ twin where the server has one). Raw answers only: the
// shapes the surfaces read are made in items.js, and the loading states in
// hooks.js. All under /api/v1 (services/api adds it).
//
// PLUGINS
//   GET    /plugins[?group_id]                          the store, each with its lifecycle here
//   GET    /plugins/<id>                                one plugin (no lifecycle)
//   GET    /plugins/status?ids=[&group_id]              lifecycles an older listing left out
//   POST   /plugins/<id>/install · DELETE …/install     install, uninstall
//   GET    /plugins/<id>/install/preview                what an uninstall takes, and leaves (new)
//   POST   /plugins/<id>/pause · …/resume               hold the whole plugin (new)
//   GET|PUT /plugins/<id>/setup-config[?tool_id]        setup: fields, realms, schedule, approval
//   POST   /plugins/<id>/setup-config/consent           approve
//   GET|DELETE /plugins/<id>/oauth · POST …/oauth/start its account
//   GET    /plugins/<id>/access | /groups/<g>/plugins/<id>/access   who may use it (new)
//   PUT    …/access/<ep_id>                             one contract's tool modes (new)
//   POST   /plugins/<id>/execute | /groups/<g>/plugins/<id>/execute
// WAITING FOR YOU (new)
//   GET    /plugin-requests?status=pending[&plugin_id] | /groups/<g>/plugin-requests
//   GET    /plugin-requests/<rid> | /groups/<g>/plugin-requests/<rid>
//   POST   /plugins/<id>/execute-requests/<rid>/approve | …/deny
// RUNS
//   GET    /plugin-runs[?plugin_id&status&outcome&started_by&tool_id] | /groups/<g>/plugin-runs
//   GET    /plugin-runs/<run> | /groups/<g>/plugin-runs/<run>
// AUTOMATIONS
//   GET|POST /automations | /groups/<g>/automations (+ /<id>, /flows, …)
// NODES (a node field's picker)
//   GET    /nodes?search&schema_id|schema_name | /groups/<g>/nodes

const enc = encodeURIComponent;
/** `?group_id=` for a group, '' for a person, joined onto any other query. */
export function scoped(path, groupId, query = {}) {
    const q = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => { if (v != null && v !== '') q.set(k, String(v)); });
    if (groupId) q.set('group_id', groupId);
    const s = q.toString();
    return s ? `${path}${path.includes('?') ? '&' : '?'}${s}` : path;
}
const bodyOf = (groupId, body = {}) => ({ ...body, ...(groupId ? { group_id: groupId } : {}) });
const qs = (query) => {
    const q = new URLSearchParams();
    Object.entries(query || {}).forEach(([k, v]) => { if (v != null && v !== '' && v !== false) q.set(k, Array.isArray(v) ? v.join(',') : String(v)); });
    const s = q.toString();
    return s ? `?${s}` : '';
};

// Where a plugin is set up from outside the section: the door an OAuth round
// trip comes back through, with the app's way back still on it.
export function setupDoor(pluginId, groupId, returnTo = null) {
    return withReturnTo(`/plugins/${enc(pluginId)}/setup${groupId ? `?group_id=${enc(groupId)}` : ''}`, returnTo);
}

export const pluginsApi = {
    list: (groupId) => api.getPlugins(groupId ? { group_id: groupId } : {}),
    get: (id) => api.request(`/plugins/${enc(id)}`),
    statuses: (ids, groupId) => api.getPluginStatuses(ids, groupId || null),
    status: (id, groupId) => api.getPluginStatus(id, groupId || null),

    setupConfig: (id, groupId, toolId = null) => api.request(scoped(`/plugins/${enc(id)}/setup-config`, groupId, { tool_id: toolId })),
    // credentials (ciphertext, `values`), settings (`params`), a schedule's
    // frequency, realm and switch, the ask-every-run switch
    save: (id, groupId, body) => api.request(`/plugins/${enc(id)}/setup-config`, { method: 'PUT', body: bodyOf(groupId, body) }),
    /** Clear saved values: `settings` and `credentials` are lists of keys, each sent as null. */
    clear: (id, groupId, { settings = [], credentials = [], toolId = null } = {}) => api.request(`/plugins/${enc(id)}/setup-config`, {
        method: 'PUT',
        body: bodyOf(groupId, {
            ...(settings.length ? { params: Object.fromEntries(settings.map((k) => [k, null])) } : {}),
            ...(credentials.length ? { values: Object.fromEntries(credentials.map((k) => [k, null])) } : {}),
            ...(toolId ? { tool_id: toolId } : {}),
        }),
    }),
    approve: (id, groupId) => api.request(`/plugins/${enc(id)}/setup-config/consent`, { method: 'POST', body: bodyOf(groupId) }),

    install: (id, groupId) => api.request(`/plugins/${enc(id)}/install`, { method: 'POST', body: bodyOf(groupId) }),
    uninstall: (id, groupId) => api.request(scoped(`/plugins/${enc(id)}/install`, groupId), { method: 'DELETE' }),
    installPreview: (id, groupId) => api.request(scoped(`/plugins/${enc(id)}/install/preview`, groupId)),
    pause: (id, groupId) => api.request(`/plugins/${enc(id)}/pause`, { method: 'POST', body: bodyOf(groupId) }),
    resume: (id, groupId) => api.request(`/plugins/${enc(id)}/resume`, { method: 'POST', body: bodyOf(groupId) }),

    oauth: (id, groupId) => api.getPluginOAuth(id, groupId || null),
    connect: (id, groupId, back) => api.startPluginOAuth(id, { groupId: groupId || null, returnTo: back }),
    disconnect: (id, groupId) => api.disconnectPluginOAuth(id, groupId || null),

    // a group's on the group's path (the route reads no ?group_id=)
    access: (id, groupId) => api.request(`${groupId ? `/groups/${enc(groupId)}` : ''}/plugins/${enc(id)}/access`),
    /** `tools`: { <tool_id>: 'allow' | 'ask' | 'deny' } on one contract. */
    setAccess: (id, groupId, epId, tools) => api.request(`${groupId ? `/groups/${enc(groupId)}` : ''}/plugins/${enc(id)}/access/${enc(epId)}`, { method: 'PUT', body: { tools } }),

    // one run of one tool, under a realm of this space; a group's on the group's path
    run: (id, groupId, { toolId, realmId, input }) => {
        const body = {
            ...(toolId ? { tool_id: toolId } : {}),
            privacy_realm_id: realmId,
            input_parameters: input || {},
        };
        return groupId
            ? api.request(`/groups/${enc(groupId)}/plugins/${enc(id)}/execute`, { method: 'POST', body })
            : api.executePlugin(id, body);
    },
};

// RUNS WAITING FOR YOU: parked runs an app or agent asked for.
export const requestsApi = {
    list: (groupId, { status = 'pending', pluginId = null } = {}) => api.request(
        `${groupId ? `/groups/${enc(groupId)}` : ''}/plugin-requests${qs({ status, plugin_id: pluginId })}`,
    ),
    get: (groupId, rid) => api.request(`${groupId ? `/groups/${enc(groupId)}` : ''}/plugin-requests/${enc(rid)}`),
    approve: (pluginId, rid, groupId) => api.request(`/plugins/${enc(pluginId)}/execute-requests/${enc(rid)}/approve`, { method: 'POST', body: bodyOf(groupId) }),
    deny: (pluginId, rid, groupId) => api.request(`/plugins/${enc(pluginId)}/execute-requests/${enc(rid)}/deny`, { method: 'POST', body: bodyOf(groupId) }),
};

// THE RUN FEED of a space, one page of it, newest first, as the server sends it:
//   personal — GET /plugin-runs (the caller's plugin runs, an automation's or not)
//   group    — GET /groups/<g>/plugin-runs (the group's, filtered to what the
//              member's realm lets them read)
// Filters (each a value or a list, comma-joined): plugin_id, status, outcome,
// started_by (a kind), tool_id. `total` counts the filtered feed and `counts`
// the whole feed per status (`outcome_counts` per outcome, when sent).
export const runsApi = {
    feed: (groupId, { pluginId = null, limit = 50, offset = 0, status = null, outcome = null, startedBy = null, toolId = null } = {}) => {
        const join = (v) => (Array.isArray(v) ? v.join(',') : v);
        const q = {
            limit,
            ...(offset ? { offset } : {}),
            ...(pluginId ? { plugin_id: String(pluginId) } : {}),
            ...(status ? { status: join(status) } : {}),
            ...(outcome ? { outcome: join(outcome) } : {}),
            ...(startedBy ? { started_by: join(startedBy) } : {}),
            // '' is a plugin's only tool: still a filter
            ...(toolId != null ? { tool_id: String(toolId) } : {}),
        };
        if (!groupId) return api.getPluginRuns(q);
        return api.request(`/groups/${enc(groupId)}/plugin-runs?${new URLSearchParams(q)}`);
    },
    get: (groupId, runId, { limit = 50 } = {}) => api.request(groupId
        ? `/groups/${enc(groupId)}/plugin-runs/${enc(runId)}?limit=${limit}`
        : `/plugin-runs/${enc(runId)}?limit=${limit}`),
};

export const autoBase = (groupId) => (groupId ? `/groups/${groupId}/automations` : '/automations');

export const autosApi = {
    list: (groupId) => (groupId ? api.request(`/groups/${groupId}/automations`) : api.getAutomations()),
    get: (groupId, id) => api.request(`${autoBase(groupId)}/${enc(id)}`),
    trigger: (groupId, id) => (groupId ? api.request(`${autoBase(groupId)}/${id}/trigger`, { method: 'POST' }) : api.triggerAutomation(id)),
    setActive: (groupId, id, on) => (groupId
        ? api.request(`${autoBase(groupId)}/${id}/${on ? 'activate' : 'deactivate'}`, { method: 'POST' })
        : on ? api.activateAutomation(id) : api.deactivateAutomation(id)),
    remove: (groupId, id) => (groupId ? api.request(`${autoBase(groupId)}/${id}`, { method: 'DELETE' }) : api.deleteAutomation(id)),
    create: (groupId, body) => (groupId ? api.request(`/groups/${groupId}/automations`, { method: 'POST', body }) : api.createAutomation(body)),
    update: (groupId, id, body) => (groupId ? api.request(`${autoBase(groupId)}/${id}`, { method: 'PUT', body }) : api.updateAutomation(id, body)),
    runs: (groupId, id, limit = 20) => api.request(`${autoBase(groupId)}/${id}/runs?limit=${limit}`),
    flows: (groupId) => api.request(`${autoBase(groupId)}/flows`),
    flow: (groupId, id) => api.request(`${autoBase(groupId)}/flows/${id}`),
    runFlow: (groupId, id) => api.request(`${autoBase(groupId)}/flows/${id}/trigger`, { method: 'POST' }),
    saveFlow: (groupId, flow, body) => (flow
        ? api.request(`${autoBase(groupId)}/flows/${flow.id}`, { method: 'PUT', body })
        : api.request(`${autoBase(groupId)}/flows`, { method: 'POST', body })),
    removeFlow: (groupId, id) => api.request(`${autoBase(groupId)}/flows/${id}`, { method: 'DELETE' }),
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A space's nodes by title, of one schema when a field declares it (by id or by name). */
export async function searchNodes(groupId, { search = '', schema = null, limit = 8 } = {}) {
    const q = { search, limit, ...(schema ? { [UUID.test(String(schema)) ? 'schema_id' : 'schema_name']: schema } : {}) };
    const r = groupId ? await api.getGroupNodes(groupId, q) : await api.getNodes(q);
    return Array.isArray(r) ? r : r?.nodes || [];
}
